import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import SubscriptionBanner from "../components/SubscriptionBanner";
import { Calculator, BookOpen, Microscope, Globe, ArrowRight, CheckCircle2, Sparkles } from "lucide-react";

const ICONS = { math: Calculator, ela: BookOpen, science: Microscope, social: Globe };
const COLOR_MAP = {
  sky:    { bg: "bg-sky-100",     text: "text-sky-700",     ring: "ring-sky-200",    chip: "bg-sky-500" },
  amber:  { bg: "bg-amber-100",   text: "text-amber-700",   ring: "ring-amber-200",  chip: "bg-amber-500" },
  emerald:{ bg: "bg-emerald-100", text: "text-emerald-700", ring: "ring-emerald-200",chip: "bg-emerald-500" },
  rose:   { bg: "bg-rose-100",    text: "text-rose-700",    ring: "ring-rose-200",   chip: "bg-rose-500" },
};

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get(`/curriculum/${user.grade}`).then(r => setData(r.data));
    api.get(`/progress`).then(r => setProgress(r.data));
  }, [user]);

  if (!user) return null;

  return (
    <div data-testid="dashboard-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <div className="mb-8">
        <SubscriptionBanner />
      </div>
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">
            <Sparkles className="inline mr-1 -mt-1" size={14} strokeWidth={3}/> Today's classroom
          </div>
          <h1 className="text-5xl lg:text-6xl font-black text-slate-900 tracking-tight">
            Hey {user.student_name.split(" ")[0]}!
          </h1>
          <p className="text-lg text-slate-600 mt-3 max-w-xl">
            Grade {user.grade} · 3 hours a day, 5 days a week. Pick a subject and let's learn something amazing.
          </p>
        </div>
        {progress && (
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Lessons done"  value={progress.completed} />
            <Stat label="Avg score"     value={`${progress.average_score}%`} />
            <Stat label="Total lessons" value={progress.total_lessons} />
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {data?.subjects?.map((subj) => {
          const Icon = ICONS[subj.key];
          const color = COLOR_MAP[subj.color] || COLOR_MAP.sky;
          const subjProg = progress?.by_subject?.[subj.key];
          const pct = subjProg && subjProg.total ? Math.round((subjProg.completed / subjProg.total) * 100) : 0;
          return (
            <div key={subj.key} data-testid={`subject-card-${subj.key}`}
                 className="bg-white rounded-3xl p-8 border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-xl transition-all">
              <div className="flex items-start justify-between mb-6">
                <div className={`w-16 h-16 rounded-2xl ${color.bg} flex items-center justify-center ring-4 ${color.ring}`}>
                  <Icon className={color.text} strokeWidth={2.5} size={28}/>
                </div>
                <div className="text-right">
                  <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Progress</div>
                  <div className="text-2xl font-black text-slate-900">{pct}%</div>
                </div>
              </div>
              <h2 className="text-3xl font-black text-slate-900 mb-1">{subj.name}</h2>
              <div className="text-sm text-slate-500 mb-5">{subj.lessons.length} lessons · VA SOL</div>

              <div className="h-2 bg-slate-100 rounded-full overflow-hidden mb-6">
                <div className={`h-full ${color.chip} transition-all`} style={{ width: `${pct}%` }} />
              </div>

              <ul className="space-y-2 mb-6 max-h-44 overflow-y-auto pr-2">
                {subj.lessons.slice(0, 5).map((l) => (
                  <li key={l.id}>
                    <Link to={`/lesson/${l.id}`} data-testid={`lesson-link-${l.id}`}
                          className="flex items-center justify-between text-sm py-2 px-3 rounded-xl hover:bg-slate-50 group">
                      <span className="font-semibold text-slate-700 group-hover:text-slate-900">
                        <span className="text-slate-400 mr-2">SOL {l.sol_code}</span>{l.title}
                      </span>
                      <ArrowRight size={14} className="text-slate-400 group-hover:text-slate-700"/>
                    </Link>
                  </li>
                ))}
              </ul>

              <Link to={`/lesson/${subj.lessons[0]?.id}`} data-testid={`subject-start-${subj.key}`}
                    className="inline-flex items-center gap-2 font-bold text-slate-900 hover:gap-3 transition-all">
                Start a lesson <ArrowRight strokeWidth={3}/>
              </Link>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white rounded-2xl px-5 py-4 border border-slate-100 min-w-[6rem] text-center">
      <div className="text-xs uppercase tracking-widest font-bold text-slate-500">{label}</div>
      <div className="text-2xl font-black text-slate-900 mt-1 flex items-center justify-center gap-1">
        <CheckCircle2 size={16} className="text-emerald-500" strokeWidth={3}/> {value}
      </div>
    </div>
  );
}
